// frontend/src/App.jsx
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import CompanyDashboard from './components/Company/Dashboard';
import IndividualDashboard from './components/Individual/Dashboard';
import History from './components/Individual/History';
import JoinRoom from './components/Individual/JoinRoom';
import CreateRoom from './components/Individual/CreateRoom';
import StealthAgent from './components/Individual/StealthAgent';
import SuperAdminLogin from './components/SuperAdmin/Login';
import SuperAdminDashboard from './components/SuperAdmin/Dashboard';
import api from './services/api';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await api.get('/auth/verify');
        setUser(response.data.user);
      } catch (error) {
        // Only clear token on a real 401 (unauthorized), not network errors
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          setUser(null);
        }
        // If network error or other issue, don't wipe the session
      }
    }
    setLoading(false);
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-white"></div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <Routes>
          <Route
            path="/login"
            element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/dashboard" replace />}
          />
          <Route
            path="/register"
            element={!user ? <Register /> : <Navigate to="/dashboard" replace />}
          />
          <Route
            path="/dashboard"
            element={
              !user ? <Navigate to="/login" replace /> :
              user.type === 'superadmin' ? <Navigate to="/super-admin/dashboard" replace /> :
              user.type === 'company' ? <CompanyDashboard user={user} onLogout={handleLogout} /> :
              <IndividualDashboard user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/history"
            element={
              user && user.type === 'user'
                ? <History user={user} onLogout={handleLogout} />
                : <Navigate to="/login" replace />
            }
          />
          <Route
            path="/join-room"
            element={
              user && user.type === 'user'
                ? <JoinRoom user={user} onLogout={handleLogout} />
                : <Navigate to="/login" replace />
            }
          />
          <Route
            path="/create-room"
            element={
              user && user.type === 'user'
                ? <CreateRoom user={user} onLogout={handleLogout} />
                : <Navigate to="/login" replace />
            }
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Super Admin routes */}
          <Route
            path="/super-admin"
            element={
              user?.type === 'superadmin'
                ? <Navigate to="/super-admin/dashboard" replace />
                : <SuperAdminLogin onLogin={handleLogin} />
            }
          />
          <Route
            path="/super-admin/dashboard"
            element={
              user?.type === 'superadmin'
                ? <SuperAdminDashboard user={user} onLogout={handleLogout} />
                : <Navigate to="/super-admin" replace />
            }
          />
          {/* Stealth agent — no auth guard, accessed via popup with URL params */}
          <Route path="/stealth" element={<StealthAgent />} />
          <Route
            path="/"
            element={
              user?.type === 'superadmin'
                ? <Navigate to="/super-admin/dashboard" replace />
                : <Navigate to={user ? "/dashboard" : "/login"} replace />
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
